import { useState, useEffect, useRef } from "react";
import { useTagDataContext } from "@/contexts/TagDataContext";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { runWithConcurrency } from "@/lib/promise-utils.js";

const TAG_DATA_BATCH_SIZE = 1000;
/** Max in-flight requests per batch; keep ≤6 to avoid Chrome net::ERR_INSUFFICIENT_RESOURCES (per-origin limit) */
const TAG_DATA_CONCURRENCY = 6;

/**
 * Custom hook to manage tag data fetching for assets in operating points page
 * Handles fetching tag data for assets and tracking loading states
 * @param {Array} assets - Array of all assets
 * @param {Array} filteredAssets - Array of filtered assets
 * @param {string} activeTab - Active tab ID
 * @param {Object} customColumns - Custom columns for each tab
 * @param {Set} assetPropertyNames - Set of asset property names that aren't tags
 * @param {Object} measurementColumns - Default measurement columns by tab
 * @param {Function} getUniversalDescriptorParameterMap - Warms UD/tag parameter maps for an asset
 * @param {Object} parameterMapsRef - Ref to parameter maps cache
 * @returns {Object} { fetchingAssetIds, setFetchingAssetIds }
 */
export function useOperatingPointsTagDataFetching(
  assets,
  filteredAssets,
  activeTab,
  customColumns,
  assetPropertyNames,
  measurementColumns,
  getUniversalDescriptorParameterMap,
  parameterMapsRef
) {
  const { getTagData, fetchTagData } = useTagDataContext();
  const { tenantRefreshKey } = useSessionTenant();
  const fetchGenerationRef = useRef(0);

  // Track which assets are currently being fetched (for loading indicators)
  const [fetchingAssetIds, setFetchingAssetIds] = useState(new Set());

  // Load tag data for active tab (on page load and when tab changes)
  // Also fetch tag data for all assets to make tags available for "Add Column" dialog
  // Optimized with parameter maps for O(1) tag lookups
  useEffect(() => {
    const generation = ++fetchGenerationRef.current;

    if (!assets || assets.length === 0) {
      setFetchingAssetIds(new Set());
      return;
    }

    // Fetch tag data for filtered assets
    // Always fetch to ensure tags are available for "Add Column" dialog
    const assetsToFetch = [];
    const currentlyLoadingIds = new Set();
    const assetsToBuildMaps = [];

    (filteredAssets || assets).forEach((asset) => {
      if (asset?.id && typeof asset.id === "string") {
        const assetId = asset.id.trim();
        const tagDataEntry = getTagData(assetId);

        // Track all currently loading assets
        if (tagDataEntry?.isLoading) {
          currentlyLoadingIds.add(assetId);
        }

        // Build parameter map if data exists (optimization for fast lookups)
        if (tagDataEntry?.tagData) {
          // Batch parameter map building instead of doing it immediately
          if (!parameterMapsRef.current.has(assetId)) {
            assetsToBuildMaps.push({ assetId, tagData: tagDataEntry.tagData });
          }
        }

        // Only fetch if we don't have data, it's not already loading, and there's no error
        // TagDataContext already prevents duplicate fetches, so we can rely on that
        // Skip assets that have errors to avoid retrying failed fetches
        if (
          !tagDataEntry?.tagData
          && !tagDataEntry?.isLoading
          && !tagDataEntry?.error
        ) {
          assetsToFetch.push(assetId);
          currentlyLoadingIds.add(assetId); // Will be loading soon
        }
      }
    });

    // Build parameter maps in batch (more efficient)
    assetsToBuildMaps.forEach(({ assetId, tagData }) => {
      getUniversalDescriptorParameterMap(assetId, tagData);
    });

    // Immediately update fetching state to show loading indicators
    setFetchingAssetIds((prev) => {
      // Only update if the set actually changed to avoid unnecessary re-renders
      if (
        prev.size !== currentlyLoadingIds.size
        || Array.from(prev).some((id) => !currentlyLoadingIds.has(id))
      ) {
        return currentlyLoadingIds;
      }
      return prev;
    });

    // Start fetching for assets that need it - batches of 1000; concurrency is enforced in TagDataContext
    if (assetsToFetch.length > 0) {
      (async () => {
        for (let i = 0; i < assetsToFetch.length; i += TAG_DATA_BATCH_SIZE) {
          if (fetchGenerationRef.current !== generation) return;
          const batch = assetsToFetch.slice(i, i + TAG_DATA_BATCH_SIZE);
          const results = await runWithConcurrency(
            batch,
            TAG_DATA_CONCURRENCY,
            (assetId) => fetchTagData(assetId)
          );
          if (fetchGenerationRef.current !== generation) return;
          results.forEach((result, index) => {
            if (result.status === "fulfilled") {
              const assetId = batch[index];
              const tagDataEntry = getTagData(assetId);
              if (
                tagDataEntry?.tagData
                && !parameterMapsRef.current.has(assetId)
              ) {
                getUniversalDescriptorParameterMap(
                  assetId,
                  tagDataEntry.tagData
                );
              }
            }
          });
        }
      })().catch((error) => {
        console.error("Unexpected error in tag data fetching:", error);
      });
    }
  }, [
    activeTab,
    assets,
    filteredAssets,
    customColumns,
    fetchTagData,
    getTagData,
    getUniversalDescriptorParameterMap,
    assetPropertyNames,
    measurementColumns,
    parameterMapsRef,
    tenantRefreshKey,
  ]);

  // Clean up parameter maps and fetching state when assets list changes (new assets added/removed)
  useEffect(() => {
    if (assets && assets.length > 0) {
      const currentAssetIds = new Set(
        assets
          .filter((asset) => asset?.id && typeof asset.id === "string")
          .map((asset) => asset.id.trim())
      );
      // Clean up parameter maps for assets that are no longer in the list
      parameterMapsRef.current.forEach((_, assetId) => {
        if (!currentAssetIds.has(assetId)) {
          parameterMapsRef.current.delete(assetId);
        }
      });
      // Also clean up fetchingAssetIds
      setFetchingAssetIds((prev) => {
        const updated = new Set(prev);
        updated.forEach((assetId) => {
          if (!currentAssetIds.has(assetId)) {
            updated.delete(assetId);
          }
        });
        return updated;
      });
    } else {
      // Clear all if no assets
      setFetchingAssetIds(new Set());
      parameterMapsRef.current.clear();
    }
  }, [assets, parameterMapsRef]);

  // Sync fetchingAssetIds with actual loading state from cache
  // This runs when cache updates (via getTagData dependency) and when tabs change
  // Optimized to only check visible assets on current page
  useEffect(() => {
    if (!assets || assets.length === 0) {
      setFetchingAssetIds((prev) => (prev.size > 0 ? new Set() : prev));
      return;
    }

    setFetchingAssetIds((prev) => {
      const updated = new Set();
      const mapsToBuild = [];

      // Only check filtered assets (visible on current page) for performance
      (filteredAssets || []).forEach((asset) => {
        if (asset?.id && typeof asset.id === "string") {
          const assetId = asset.id.trim();
          const tagDataEntry = getTagData(assetId);
          if (tagDataEntry?.isLoading) {
            // Currently loading, add to fetching set
            updated.add(assetId);
          } else if (tagDataEntry?.tagData) {
            // Data loaded, build parameter map if not already cached
            if (!parameterMapsRef.current.has(assetId)) {
              mapsToBuild.push({ assetId, tagData: tagDataEntry.tagData });
            }
          }
          // If no data and not loading, don't add to fetching set
        }
      });

      // Build parameter maps in batch
      mapsToBuild.forEach(({ assetId, tagData }) => {
        getUniversalDescriptorParameterMap(assetId, tagData);
      });

      // Only update state if the set actually changed to avoid unnecessary re-renders
      if (
        prev.size !== updated.size
        || Array.from(prev).some((id) => !updated.has(id))
        || Array.from(updated).some((id) => !prev.has(id))
      ) {
        return updated;
      }
      return prev;
    });
  }, [
    filteredAssets,
    getTagData,
    getUniversalDescriptorParameterMap,
    assets,
    parameterMapsRef,
  ]);

  return { fetchingAssetIds, setFetchingAssetIds };
}
